import { useQuery } from '@tanstack/react-query';

import {
  getRequest,
  paginationMapper,
  resolveQueryString,
} from 'src/utils/request';
import { locale } from 'src/config';
import { API } from 'src/reactQuery/Api';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import { toParsableNumber } from 'src/utils/standards';

import type { RequestDataArray } from 'src/types/requestData';
import type { CandidateStatusType } from 'src/pages/CandidatesPage';

const mapCandidates = (
  attributes: UCM.AllCandidatesRawType,
  id: string,
): UCM.AllCandidatesType => ({
  id,
  uuid: attributes.uuid,
  firstName: attributes.first_name,
  lastName: attributes.last_name,
  email: attributes.email,
  genderName: attributes.gender_name,
  statusCode: attributes.status_code,
  roleCode: attributes.role_code,
  status: attributes.status,
  contractTypeCode: attributes.contract_type_code,
  phoneNumbers: attributes.phone_numbers,
  cities: attributes.cities,
  languages: attributes.languages,
});

const getAllCandidates = async (
  filters: Array<{
    value?: Array<string> | string | null;
    name?: string | null;
    operator?: string | null;
    label?: string | null;
  }> = [],
  params: Record<string, string | number>,
) => {
  // Format numeric values for backend while preserving original filters
  const formattedFilters = filters.map((filter) => {
    if (filter.name === 'Rating' && typeof filter.value === 'string') {
      return {
        ...filter,
        value: toParsableNumber(filter.value, locale),
      };
    }

    return filter;
  });

  const query = resolveQueryString([formattedFilters], params);

  const {
    data: { data, meta },
  } = await getRequest<
    RequestDataArray<UCM.AllCandidatesRawType, UCM.MetaRawType>
  >(`${API.GET.getCandidates}${query}`);

  return {
    meta: paginationMapper(meta),
    data: data.map(({ attributes, id }) => mapCandidates(attributes, id)),
  };
};

export const useGetAllCandidates = (
  filters: Array<
    | {
        value?: Array<string> | string | null;
        name?: string | null;
        operator?: string | null;
        label?: string | null;
      }
    | {
        name: 'section';
        value: CandidateStatusType;
      }
  > = [],
  status: CandidateStatusType,
  params: Record<string, string | number>,
  config: { enabled?: boolean } = { enabled: true },
) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ALL_CANDIDATES, filters, params, status],
    queryFn: () => getAllCandidates(filters, params),
    ...config,
  });
};
